import { Modal, type ModalSize } from "@/components/Modal";
import { createElement, useEffect, useRef, useState, type JSXElementConstructor, type ReactNode } from "react";
import { createRoot, type Root } from "react-dom/client";
import { RouterContextProvider } from "@tanstack/react-router";
import { router } from "@/router";

export interface ModalActions {
    open: () => void;
    close: () => void;
}

export interface ModalOptions {
    title?: string;
    size?: ModalSize;
    fixedSize?: boolean;
    hasCloseButton?: boolean;
    headerContent?: ReactNode;
}

export function useModal<T extends JSXElementConstructor<any>>(
    component: T,
    props: Omit<React.ComponentProps<T>, keyof ModalActions>,
    options?: ModalOptions
): ModalActions {
    const [isOpen, setIsOpen] = useState(false);
    const rootRef = useRef<Root | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const open = () => {
        setIsOpen(true);
    }
    const close = () => {
        setIsOpen(false);
    }

    useEffect(() => {
        // Only run in browser environment
        if (typeof document === 'undefined') return;

        if (isOpen) {
            // Clear any pending cleanup
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            // Create container if it doesn't exist
            if (!containerRef.current) {
                containerRef.current = document.createElement('div');
                document.body.appendChild(containerRef.current);
            }

            // Create root if it doesn't exist
            if (!rootRef.current) {
                rootRef.current = createRoot(containerRef.current);
            }

            // Render the component wrapped in Modal
            const componentProps = {
                ...props,
                open,
                close,
            };
            const componentElement = createElement(component, componentProps);
            const modalElement = createElement(
                Modal,
                {
                    onClose: close,
                    title: options?.title || "",
                    size: options?.size,
                    children: componentElement,
                    hasCloseButton: options?.hasCloseButton,
                    headerContent: options?.headerContent
                }
            );
            // The modal renders in a separate React root (createRoot above), which
            // lives outside the app's <RouterProvider>. Re-provide the router via
            // context so modal content that uses TanStack Router — e.g. the <Link>s
            // in the makers board embedded by the Find People modal — reads the real
            // router instead of a null context (which throws "reading 'stores'").
            const rootElement = createElement(
                RouterContextProvider,
                { router, children: modalElement },
            );
            rootRef.current.render(rootElement);
        }

        // Cleanup function - defer unmounting to avoid race condition
        return () => {
            // Clear any pending timeouts
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Defer unmounting to avoid race condition with React rendering
            timeoutRef.current = setTimeout(() => {
                if (rootRef.current) {
                    rootRef.current.unmount();
                    rootRef.current = null;
                }
                if (containerRef.current && containerRef.current.parentNode) {
                    document.body.removeChild(containerRef.current);
                    containerRef.current = null;
                }
                timeoutRef.current = null;
            }, 0);
        };
    }, [isOpen, component, props, options]);

    return {
        open,
        close,
    };
}